import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { UseFormReturn } from "react-hook-form";
import { toast } from "sonner";
import { AuthContext } from "@/hooks/auth/AuthContext";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { BuildingCreationValues } from "@/domains/buildings/components/form/buildingCreationSchema";
import type { BuildingPlanTier } from "./types";
import {
  clearBuildingSetupDraft,
  readBuildingSetupDraft,
  saveBuildingSetupDraft,
} from "./buildingSetupDraft";

function draftValues(values: BuildingCreationValues) {
  return {
    name: values.name ?? "",
    address: values.address ?? "",
    registry_code: values.registry_code ?? "",
  };
}

export function useBuildingSetupDraft(
  orgId: string,
  form: UseFormReturn<BuildingCreationValues>,
  tier: BuildingPlanTier,
  setTier?: (tier: BuildingPlanTier) => void
) {
  const userId = useContext(AuthContext)?.userId;
  const { t } = useLanguage();
  const navigate = useNavigate();
  const restored = useRef(false);
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    if (restored.current || !setTier) return;
    const draft = readBuildingSetupDraft(userId, orgId);
    if (!draft) return;
    restored.current = true;
    form.reset(draft.values);
    setTier(draft.tier);
  }, [form, orgId, setTier, userId]);
  useEffect(() => {
    if (leaving) navigate("/billing/details");
  }, [leaving, navigate]);
  const startSetup = () => {
    const values = form.getValues();
    if (
      !userId ||
      !saveBuildingSetupDraft({
        userId,
        orgId,
        tier,
        createdAt: Date.now(),
        values: draftValues(values),
      })
    ) {
      toast.error(t.billing.details.draftSaveFailed);
      return;
    }
    form.reset(values);
    setLeaving(true);
  };
  return { startSetup, clearDraft: () => clearBuildingSetupDraft(userId, orgId) };
}
